import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
})

function AuthCallback() {
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)
  const navigate = useNavigate()
  const processedRef = useRef(false)

  useEffect(() => {
    // Evita dupla execução em React StrictMode
    if (processedRef.current) return
    processedRef.current = true

    async function handleAuthCallback() {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const error = urlParams.get('error')
        const errorDescription = urlParams.get('error_description')

        if (error) {
          throw new Error(errorDescription || error)
        }

        if (code) {
          // Troca o código pelo token JWT usando o code_verifier salvo no cliente (PKCE)
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
          if (exchangeError) {
            throw exchangeError
          }

          // Higienização imediata da URL para evitar que o código fique no histórico ou cabeçalhos Referer
          if (typeof window !== 'undefined') {
            window.history.replaceState({}, document.title, window.location.pathname)
          }

          // Redireciona para a raiz para que o AuthProvider decida a rota (app, master ou onboarding)
          navigate({ to: '/' })
          return
        }

        // Se não veio code, verifica se já existe sessão ativa
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (session) {
          navigate({ to: '/' })
        } else {
          throw new Error('Nenhum código de autorização foi encontrado na requisição.')
        }
      } catch (err: any) {
        console.error('[Auth Callback Error]', err)
        setErrorMsg(err.message || 'Falha na validação da autenticação com o provedor.')
        setIsProcessing(false)
      }
    }

    handleAuthCallback()
  }, [navigate])

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900/80 border border-white/10 rounded-3xl p-8 backdrop-blur-2xl shadow-2xl text-center">
        {isProcessing && !errorMsg ? (
          <div className="space-y-6">
            <div className="relative inline-flex items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-2">Validando Credenciais</h3>
              <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto">
                Concluindo autenticação segura via chave de troca criptográfica (PKCE)...
              </p>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Proteção contra interceptação ativa
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400 mx-auto">
              <AlertCircle className="w-8 h-8" />
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-2">Erro na Autenticação</h3>
              <p className="text-xs text-rose-300 leading-relaxed bg-rose-500/10 border border-rose-500/20 p-3 rounded-xl mb-4">
                {errorMsg}
              </p>
            </div>

            <Link to="/login" className="block w-full">
              <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold h-12 rounded-xl text-sm shadow-lg shadow-blue-500/25">
                Voltar para o Login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
